import { Component, computed, ElementRef, inject, ViewChild } from '@angular/core';
import { ProgressService } from '../../lib/progress.service';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { ProgressInfoComponent } from '../../progress-info/progress-info.component';
import {
  Address,
  COUNTRIES,
  Day,
  Product,
  ReservationRefererTypes,
  ReservationRefererTypesMapper,
} from '@shared';
import { Router } from '@angular/router';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { InputerrorsComponent } from '../../inputerrors.component';
import { ApiService } from '../../lib/api.service';

@Component({
  selector: 'app-booking-summary',
  imports: [
    CurrencyPipe,
    ProgressInfoComponent,
    DatePipe,
    ReactiveFormsModule,
    FormsModule,
    InputerrorsComponent,
  ],
  templateUrl: './booking-summary.component.html',
  styleUrl: './booking-summary.component.css',
})
export class BookingSummaryComponent {
  public progressService = inject(ProgressService);
  public router = inject(Router);
  public apiService = inject(ApiService);
  public countries = COUNTRIES;
  @ViewChild('pickupTimeElem') pickupTimeElement: undefined|ElementRef;
  public refererOptions = computed<{ value: string; label: string }[]>(() =>
    Object.keys(ReservationRefererTypesMapper).map((objectKey: string) => {
      return {
        label:
          ReservationRefererTypesMapper[objectKey as ReservationRefererTypes],
        value: objectKey,
      };
    }),
  );
  public arrivalOptions = computed<Date[]>(() => {
    const startTime = new Date(
      `${this.selectedDaySlot()?.date} ${this.selectedDaySlot()?.time_from}`,
    );
    const endTime = new Date(
      `${this.selectedDaySlot()?.date} ${this.selectedDaySlot()?.time_latest}`,
    );
    const resultingTimes = [];

    for (
      let currentTime = startTime;
      currentTime <= endTime;
      currentTime.setMinutes(currentTime.getMinutes() + 30)
    ) {
      resultingTimes.push(new Date(currentTime));
    }

    return resultingTimes;
  });
  public grandTotal = computed<number>(
    () =>
      this.tokenSum() +
      this.basePriceSum() +
      (this.progressService.selectedShipping()?.price || 0),
  );
  public tokenSum = computed<number>(() =>
    this.bookedProducts()
      .map(
        (bookedProduct) =>
          +(bookedProduct.pivot!.amount || 0) *
          (+bookedProduct.price + +(bookedProduct.bediengeld || 0)),
      )
      .reduce((prev, cur) => prev + cur, 0),
  );
  public bookedProducts = computed<Product[]>(
    () =>
      this.progressService
        .selectedAdditionalProducts()
        ?.filter((product) => +(product.pivot!.amount || 0) > 0) || [],
  );
  public basePriceSum = computed<number>(() => {
    if (!this.selectedDaySlot()) {
      return 0;
    }

    let pricePerPerson = 0;

    if (this.progressService.selectedPackage()) {
      pricePerPerson =
        +this.progressService.selectedPackage()!.price +
        (this.progressService
          .selectedPackage()!
          .products?.map((product) => +(product.bediengeld || 0))
          .reduce((prev, cur) => prev + cur, 0) || 0);
    } else {
      pricePerPerson = this.getCostForProducts(
        this.selectedDaySlot()?.products || [],
      );
    }

    return pricePerPerson * (this.progressService.selectedPersons() || 0);
  });
  public selectedDaySlot = computed<Day | null>(() => {
    if (!this.progressService.selectedLodge()) {
      return null;
    }

    return (
      this.progressService
        .selectedLodge()
        ?.days?.find(
          (daySlot) =>
            daySlot.daytype === this.progressService.selectedDayslot() &&
            daySlot.date === this.progressService.selectedDate(),
        ) || null
    );
  });
  public invoiceForm = new FormGroup({
    customer_type: new FormControl('', [Validators.required]),
    customer_nr_old: new FormControl(''),
    phone: new FormControl(''),
    mobile: new FormControl(''),
    email: new FormControl('', [Validators.required, Validators.email]),
    salutation: new FormControl('none'),
    title: new FormControl(''),
    name: new FormControl('', [Validators.required]),
    surname: new FormControl('', [Validators.required]),
    typename: new FormControl(''),
    street: new FormControl('', [Validators.required]),
    street_nr: new FormControl('', [Validators.required]),
    street_extra: new FormControl(''),
    zipcode: new FormControl('', [Validators.required]),
    city: new FormControl('', [Validators.required]),
    country: new FormControl(
      '',
      Validators.compose([
        Validators.required,
        this.expectValueOneOf(
          this.progressService.selectedShipping()
            ?.requiresCountryCodeInDeliveryAdress || [],
          true,
        ),
      ]),
    ),
    address_same: new FormControl(true),
    reservation_name: new FormControl(''),
    referer: new FormControl(''),
  });
  public deviatingDeliveryAdressForm = new FormGroup({
    typename: new FormControl(''),
    salutation: new FormControl('none'),
    name: new FormControl('', [Validators.required]),
    surname: new FormControl('', [Validators.required]),
    street: new FormControl('', [Validators.required]),
    street_nr: new FormControl('', [Validators.required]),
    zipcode: new FormControl('', [Validators.required]),
    city: new FormControl('', [Validators.required]),
    street_extra: new FormControl(''),
    country: new FormControl(
      '',
      Validators.compose([
        Validators.required,
        this.expectValueOneOf(
          this.progressService.selectedShipping()
            ?.requiresCountryCodeInDeliveryAdress || [],
        ),
      ]),
    ),
  });
  public deliveryAdressEqualsInvoiceAdress = true;
  public additionalComment = "";
  public referer = "";
  public acceptTermsAndConditions = false;
  public triedToSubmit = false;
  public pickupTime = '';

  expectValueOneOf(
    validValues: (string | number)[],
    overriddenByDeviatingDeliveryAdress = false,
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (validValues.length == 0) {
        return null;
      }
      if (
        overriddenByDeviatingDeliveryAdress &&
        !this.deliveryAdressEqualsInvoiceAdress
      ) {
        return null;
      }

      const val = control.value;

      if (validValues.includes(val)) {
        return null;
      }

      return {
        expectValueOneOf: { expectedOneOf: validValues, actual: control.value },
      };
    };
  }

  getCostForProducts(products: Product[]) {
    return products
      ?.map(
        (product) =>
          +(product.pivot?.amount || 1) *
          (+product.price + +(product.bediengeld || 0)),
      )
      .reduce((prev, cur) => prev + cur, 0);
  }

  confirmBooking() {
    this.invoiceForm.markAllAsTouched();
    this.deviatingDeliveryAdressForm.markAllAsTouched();
    this.triedToSubmit = true;

    if(!this.pickupTime) {
      this.pickupTimeElement?.nativeElement.scrollIntoView({ behavior: "smooth", block: "start"})
      return;
    }

    if(!this.acceptTermsAndConditions) {
      return;
    }

    if (
      this.grandTotal() > 1 &&
      this.invoiceForm.valid &&
      (this.deliveryAdressEqualsInvoiceAdress ||
        this.deviatingDeliveryAdressForm.valid)
    ) {
      const addresses: { defaultAddress?: Address; invoiceAddress?: Address } =
        {};

      if (this.deliveryAdressEqualsInvoiceAdress) {
        // @ts-ignore
        addresses.defaultAddress = {
          ...this.invoiceForm.value,
          address_type: 'default',
          // @ts-ignore
          title: '',
        };
      } else {
        // @ts-ignore
        addresses.defaultAddress = {
          ...this.deviatingDeliveryAdressForm.value,
          address_type: 'delivery',
          // @ts-ignore
          title: '',
        };
        // @ts-ignore
        addresses.invoiceAddress = {
          ...this.invoiceForm.value,
          address_type: 'invoice',
          // @ts-ignore
          title: '',
        };
      }

      this.apiService
        .submitBookingRequest({
          ...addresses,
          selectedFestival: {
            festivalId: this.progressService.selectedFestival()!.id || 0,
            festivalTitle:
              this.progressService.selectedFestival()!.title_public ||
              this.progressService.selectedFestival()!.title,
          },
          selectedLodge: {
            lodgeId: this.progressService.selectedLodge()!.id || 0,
            lodgeTitle:
              this.progressService.selectedLodge()!.title_public ||
              this.progressService.selectedLodge()!.title,
            lodgeTableFactor: [],
            lodgeProducts: this.progressService
              .selectedAdditionalProducts()
              .map((product) => {
                product.amount = product.pivot?.amount;
                return product;
              }),
          },
          selectedDate: this.progressService.selectedDate()!,
          selectedDaytime: this.progressService.selectedDayslot()!,
          selectedPersons: this.progressService.selectedPersons()!,
          selectedDay: {
            type: this.progressService.selectedPackage()
              ? 'package'
              : 'product',
            // @ts-ignore
            day: this.selectedDaySlot()!,
          },
          selectedPickupTime: this.pickupTime,
          selectedDelivery: this.progressService.selectedShipping()!,
          customerData: {
            customer_nr_old:
              this.invoiceForm.controls.customer_nr_old.value || null,
            // @ts-ignore
            customer_type: this.invoiceForm.controls.customer_type.value,
            email: this.invoiceForm.controls.email.value || undefined,
            phone: this.invoiceForm.controls.phone.value || undefined,
            mobile: this.invoiceForm.controls.mobile.value || undefined,
          },
          selectedDefaultPackage:
            this.progressService.selectedPackage() || undefined,
          selectedDefaultProducts: this.selectedDaySlot()!.products,
          referer: this.referer,
          notes: this.additionalComment
        })
        .subscribe({
          next: (data) => {
            this.router.navigate(['/success']);
          },
          error: (e) => {
            alert(
              'Bei der übermittlung der Reservierung ist ein Fehler aufgetreten. Bitte erneut versuchen',
            );
            console.error(e);
          },
        });
    } else {
      const firstErrorElem = document.querySelector('form .ng-invalid');
      if (firstErrorElem) {
        firstErrorElem.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
        return;
      }

      if (this.grandTotal() < 1) {
        alert(
          'Da ist leider etwas sehr schiefgelaufen. Bitte laden Sie die Seite erneut',
        );
        return;
      }
    }
  }
}
